/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { styled } from '@aibi-ui/core';
import TagType from 'src/types/TagType';
import { Tag as AntdTag } from 'antd-v5';
import { useMemo } from 'react';
import { Tooltip } from 'src/components/Tooltip';
import { CloseOutlined } from '@ant-design/icons';

const StyledTag = styled(AntdTag)<{ tagType: string }>(({ theme, tagType }) => {
  let color = theme.colors.primary.base;
  let borderColor = theme.colors.primary.base;
  let background = 'transparent';

  if (tagType === 'primary') {
    color = theme.colors.primary.base;
    borderColor = theme.colors.primary.base;
    background = '#eef3ff';
  } else if (tagType === 'success') {
    color = theme.colors.success.base;
    borderColor = theme.colors.success.base;
    background = '#e3f9e9';
  }

  return {
    padding: '0 10px',
    height: '28px',
    lineHeight: '28px',
    fontSize: '14px',
    marginTop: theme.gridUnit,
    marginBottom: theme.gridUnit,
    color,
    border: `1px solid ${borderColor}`,
    borderRadius: 3,
    background,
  };
});

export const CustomCloseIcon = <CloseOutlined role="button" />;

const MAX_DISPLAY_CHAR = 20;

const Tag = ({
  name,
  id,
  index = undefined,
  onDelete = undefined,
  editable = false,
  onClick = undefined,
  toolTipTitle = name,
  color,
  tagType = 'default',
}: TagType) => {
  const isLongTag = useMemo(() => name.length > MAX_DISPLAY_CHAR, [name]);
  const tagDisplay = isLongTag ? `${name.slice(0, MAX_DISPLAY_CHAR)}...` : name;

  const handleClose = () => (index ? onDelete?.(index) : null);

  const whatRole = onClick ? (!id ? 'button' : 'link') : undefined;

  const tagElem = (
    <>
      {editable ? (
        <Tooltip title={toolTipTitle} key={toolTipTitle}>
          <StyledTag
            key={id}
            closable={editable}
            onClose={handleClose}
            color={color}
            closeIcon={editable ? CustomCloseIcon : undefined}
            tagType={tagType}
          >
            {tagDisplay}
          </StyledTag>
        </Tooltip>
      ) : (
        <Tooltip title={toolTipTitle} key={toolTipTitle}>
          <StyledTag
            tagType={tagType}
            data-test="tag"
            key={id}
            onClick={onClick}
            role={whatRole}
          >
            {id ? (
              <a
                href={`/aibi/all_entities/?id=${id}`}
                target="_blank"
                rel="noreferrer"
              >
                {tagDisplay}
              </a>
            ) : (
              tagDisplay
            )}
          </StyledTag>
        </Tooltip>
      )}
    </>
  );

  return tagElem;
};

export default Tag;
